import { forwardRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Triangle from '../Triangle/Triangle';
import { Button } from '../Button/Button';
import { Title } from '../Title/Title';
import LeftImage from './LeftImage/LeftImage';
import CompleteImage from './assets/ContacImageComplete.png';
import ContactForm from './ContactForm/ContactForm';
import { useWindowWidth } from '../../../../../hooks/useWindowWidth';

const Contact = forwardRef<HTMLDivElement>((props, ref) => {
  const [orbit, setOrbit] = useState(false);
  const textButton = 'Send us an email';
  const title = 'Do you need help?';
  const buttonId = 'Contact_sendUsAnEmail_Button';
  const width = useWindowWidth();

  return (
    <Background ref={ref}>
      <Triangle color='white' />
      <Wrapper>
        <CustomLandingWrapper>
          <Section>
            {width >= 1024 ? (
              <LeftImage orbit={orbit} />
            ) : (
              <LeftCompleteImage src={CompleteImage} $visible={!orbit} />
            )}
            <RightWrapper $visible={!orbit}>
              <FormBlock $visible={!orbit}>
                <FormTextWrapper>
                  <FormTitle text={title} size={width < 1024 ? 'small' : undefined} />
                  <Button maxWidth='245px' onClick={() => setOrbit(true)} id={buttonId}>
                    {textButton}
                  </Button>
                </FormTextWrapper>
              </FormBlock>

              {orbit && (
                <FormBlock2 $visible={orbit}>
                  <ContactForm orbit={orbit} />
                </FormBlock2>
              )}
            </RightWrapper>
          </Section>
        </CustomLandingWrapper>
      </Wrapper>
    </Background>
  );
});

export default Contact;

const Background = styled.div`
  width: 100%;
  background-color: white;
  position: relative;
`;

const Wrapper = styled.div`
  overflow: hidden;
  width: 100%;
  height: auto;
  padding-top: 50px;
  min-height: 600px;

  @media (min-width: 768px) {
    min-height: auto;
  }

  @media (min-width: 1024px) {
    padding-top: 200px;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: auto;
  max-width: 1220px;
  gap: 40px;

  @media (min-width: 768px) {
    flex-direction: row;
    min-height: 520px;
    gap: 15px;
  }

  @media (min-width: 1024px) {
    flex-direction: row;
    height: 500px;
    gap: 20px;
  }
`;

const RightWrapper = styled.div<{ $visible: boolean }>`
  display: flex;
  width: 100%;
  justify-content: center;

  @media (min-width: 768px) {
    position: relative;
    max-width: ${({ $visible }) => ($visible ? '100%' : '0')};
    width: auto;
    height: 100%;
    display: flex;
    align-items: center;
  }

  @media (min-width: 1024px) {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 60px;
    max-width: 520px;
  }
`;

const FormBlock = styled.div<{ $visible: boolean }>`
  width: ${({ $visible }) => ($visible ? '100%' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  display: flex;
  justify-content: center;
  aling-items: center;
`;

const slideFadeIn = keyframes`
  0% {
    transform: translateX(-50%) translateY(150px);
    opacity: 0;
  }
  100% {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
`;

const FormBlock2 = styled.div<{ $visible: boolean }>`
  @media (max-width: 640px) {
    width: ${({ $visible }) => ($visible ? '100%' : '0')};
    transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  }

  @media (min-width: 1024px) {
    position: absolute;
    top: 0;
    left: 50%;
    width: 100%;
    max-width: 700px;
    height: auto;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

    animation: ${({ $visible }) => ($visible ? slideFadeIn : 'none')} 0.5s
      cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
`;

const FormTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #fff6ef;
  gap: 20px;
  padding: 70px 60px;
  border-radius: 150px 0 150px 0;
  margin-top: 10px;
  max-width: 400px;
  width: 400px;

  @media (min-width: 768px) {
    height: auto;
    margin: 140px 0 0 -50px;
  }

  @media (min-width: 1024px) {
    max-width: 700px;
    height: auto;
    box-sizing: border-box;
    margin: 0;
  }
`;

const FormTitle = styled(Title)`
  font-size: 15px;

  @media (min-width: 768px) {
    font-size: clamp(24px, 3vw, 34px);
  }

  @media (min-width: 1024px) {
    font-size: clamp(32px, 4vw, 55px);
  }
`;

const CustomLandingWrapper = styled.div`
  all: unset;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;

  @media (min-width: 1024px) {
    padding: 0 60px;
  }
`;

const LeftCompleteImage = styled.img<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  width: 300px;

  @media (min-width: 768px) {
    position: relative;
    width: 360px;
    top: 76px;
    margin-bottom: -5px;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;
